// pages/FileCollaborationPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL, SOCKET_URL as SOCKET_URL_CONFIG } from '../config/api';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import { Users, FileText, Loader2, Save, X, Upload, File, FileUp, FileDown, Type, Image as ImageIcon, Star, Clock, MoreVertical, Share2, Download, MessageSquare } from 'lucide-react';
import RichTextCollaborativeEditor from '../components/RichTextCollaborativeEditor';
import PDFCollaborativeViewer from '../components/PDFCollaborativeViewer';
import WordFileImporter from '../components/WordFileImporter';
import mammoth from 'mammoth';

const API_BASE = API_BASE_URL;
const SOCKET_URL = SOCKET_URL_CONFIG || window.location.origin;

interface FileCollaborationPageProps {
  projectId: string;
  user: any;
}

const FileCollaborationPage: React.FC<FileCollaborationPageProps> = ({ projectId, user }) => {
  const [content, setContent] = useState('');
  const [activeUsers, setActiveUsers] = useState<Array<{ userId: string; userName: string }>>([]);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState('document-collaboratif');
  const [fileType, setFileType] = useState<'text' | 'rich-text' | 'pdf' | 'word'>('rich-text');
  const [initialContent, setInitialContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const lastContentRef = useRef<string>('');

  useEffect(() => {
    // Initialiser Socket.io
    const token = localStorage.getItem('token');
    console.log('[FileCollab] Tentative de connexion Socket.io à:', SOCKET_URL);
    
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    socketRef.current = socket;

    const roomId = `file-collab-${projectId}`;

    socket.on('connect', () => {
      console.log('[FileCollab] ✅ Socket connecté, socket.id:', socket.id);
      setConnected(true);
      
      // Rejoindre la room de collaboration
      console.log('[FileCollab] Envoi file-collab:join pour projectId:', projectId);
      socket.emit('file-collab:join', { projectId, roomId });
    });

    socket.on('connect_error', (error) => {
      console.error('[FileCollab] ❌ Erreur de connexion Socket.io:', error);
      setError('Server connection error. Check that the server is running.');
    });

    socket.on('disconnect', (reason) => {
      console.log('[FileCollab] ⚠️ Socket déconnecté:', reason);
      setConnected(false);
    });

    socket.on('file-collab:joined', (data: { activeUsers: any[]; content?: string }) => {
      console.log('[FileCollab] ✅ Rejoint avec succès:', data);
      setActiveUsers(data.activeUsers || []);
      if (data.content !== undefined) {
        setContent(data.content);
        lastContentRef.current = data.content;
        if (textareaRef.current) {
          textareaRef.current.value = data.content;
        }
      }
    });

    socket.on('file-collab:user-joined', (data: { userId: string; userName: string }) => {
      console.log('[FileCollab] Utilisateur rejoint:', data);
      setActiveUsers(prev => {
        if (!prev.find(u => u.userId === data.userId)) {
          return [...prev, { userId: data.userId, userName: data.userName }];
        }
        return prev;
      });
    });

    socket.on('file-collab:user-left', (data: { userId: string }) => {
      console.log('[FileCollab] Utilisateur parti:', data);
      setActiveUsers(prev => prev.filter(u => u.userId !== data.userId));
    });

    socket.on('file-collab:content-update', (data: { content: string; userId: string }) => {
      // Ne pas mettre à jour si c'est notre propre modification
      if (data.userId !== user._id) {
        setContent(data.content);
        lastContentRef.current = data.content;
        // Mettre à jour le textarea
        if (textareaRef.current) {
          textareaRef.current.value = data.content;
        }
      }
    });

    socket.on('error', (error: { message: string }) => {
      console.error('[FileCollab] ❌ Erreur Socket:', error);
      setError(error.message || 'Erreur de communication avec le serveur');
    });

    // Charger le contenu initial
    const loadContent = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/projects/${projectId}/collaborative-file`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (res.data.success && res.data.data) {
          const fileData = res.data.data;
          const loadedContent = fileData.content || '';
          setContent(loadedContent);
          setInitialContent(loadedContent);
          setFileName(fileData.fileName || 'document-collaboratif.txt');
          lastContentRef.current = loadedContent;
          
          // Détecter le type de fichier
          const ext = (fileData.fileName || '').toLowerCase().split('.').pop();
          
          // Si le contenu contient du HTML (balises HTML), utiliser l'éditeur riche
          const hasHtmlTags = /<[a-z][\s\S]*>/i.test(loadedContent);
          
          if (ext === 'pdf') {
            setFileType('pdf');
          } else if (['doc', 'docx'].includes(ext || '')) {
            // Si c'est un fichier Word et qu'il contient du HTML, c'est qu'il a été converti
            if (hasHtmlTags) {
              setFileType('rich-text');
            } else {
              setFileType('word'); // Sinon, afficher l'importeur
            }
          } else if (hasHtmlTags) {
            // Si le contenu contient du HTML, utiliser l'éditeur riche
            setFileType('rich-text');
          } else {
            // Par défaut, utiliser l'éditeur riche même pour .txt
            setFileType('rich-text');
          }
        }
      } catch (err: any) {
        // Si le fichier n'existe pas encore, on commence avec un contenu vide
        if (err.response?.status !== 404) {
          console.error('Erreur chargement contenu:', err);
        }
      }
    };

    loadContent();

    return () => {
      // Nettoyage
      if (socketRef.current) {
        socketRef.current.emit('file-collab:leave', { projectId, roomId });
        socketRef.current.disconnect();
      }
    };
  }, [projectId, user._id]);

  // Callback pour les changements de contenu de l'éditeur riche
  const handleRichTextContentChange = useCallback((html: string) => {
    setContent(html);
    // Envoyer la mise à jour via Socket.io
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('file-collab:content-change', {
        projectId,
        content: html,
        userId: user._id
      });
    }
  }, [projectId, user._id]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    
    // Envoyer la mise à jour aux autres utilisateurs
    if (socketRef.current && socketRef.current.connected && newContent !== lastContentRef.current) {
      socketRef.current.emit('file-collab:content-change', {
        projectId,
        content: newContent,
        userId: user._id
      });
      lastContentRef.current = newContent;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/projects/${projectId}/collaborative-file/save`,
        {
          content,
          fileName
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      alert('File saved successfully');
    } catch (err: any) {
      console.error('Erreur sauvegarde:', err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            {/* Icône de type de fichier avec badge coloré */}
            <div className="relative">
              {fileType === 'pdf' ? (
                <div className="p-2 bg-red-50 rounded-lg border border-red-200">
                  <FileText className="h-6 w-6 text-red-600" />
                </div>
              ) : fileType === 'word' ? (
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <Type className="h-6 w-6 text-blue-600" />
                </div>
              ) : (
                <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
              )}
              {/* Badge de type */}
              <div className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                fileType === 'pdf' ? 'bg-red-500' :
                fileType === 'word' ? 'bg-blue-500' :
                'bg-indigo-500'
              }`}>
                {fileType === 'pdf' ? 'PDF' :
                 fileType === 'word' ? 'DOC' :
                 fileType === 'rich-text' ? 'RTF' : 'TXT'}
              </div>
            </div>
            
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{fileName}</h2>
              <div className="flex items-center gap-3 mt-0.5">
                {connected ? (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    {activeUsers.length > 0 ? `${activeUsers.length} online` : 'Connected'}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Connexion...
                  </span>
                )}
                {activeUsers.length > 0 && (
                  <>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">
                      {activeUsers.length} {activeUsers.length > 1 ? 'collaborateurs' : 'collaborateur'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Sélecteur de type de fichier avec icônes */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
            <button
              onClick={() => setFileType('rich-text')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                fileType === 'rich-text'
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-4 w-4" />
              <span>Riche</span>
            </button>
            <button
              onClick={() => setFileType('text')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                fileType === 'text'
                  ? 'bg-gray-100 text-gray-700 border border-gray-300'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="h-4 w-4" />
              <span>Texte</span>
            </button>
            <button
              onClick={() => setFileType('pdf')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                fileType === 'pdf'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <ImageIcon className="h-4 w-4" />
              <span>PDF</span>
            </button>
            <button
              onClick={() => setFileType('word')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                fileType === 'word'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Type className="h-4 w-4" />
              <span>Word</span>
            </button>
          </div>

          {/* Bouton upload fichier avec style moderne */}
          <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 cursor-pointer transition-all shadow-sm hover:shadow-md">
            <Upload className="h-4 w-4" />
            <span className="text-sm font-medium">Importer</span>
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                const ext = file.name.toLowerCase().split('.').pop();
                setFileName(file.name);

                if (ext === 'pdf') {
                  setFileType('pdf');
                  // Pour PDF, on doit l'uploader au backend
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    const res = await axios.post(
                      `${API_BASE}/projects/${projectId}/collaborative-file/upload`,
                      formData,
                      {
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'multipart/form-data'
                        }
                      }
                    );
                    if (res.data.success) {
                      alert('PDF file uploaded successfully');
                    }
                  } catch (err) {
                    console.error('Erreur upload PDF:', err);
                    alert('Erreur lors de l\'upload du fichier PDF');
                  }
                } else if (['doc', 'docx'].includes(ext || '')) {
                  // Convertir le fichier Word en HTML
                  try {
                    const arrayBuffer = await file.arrayBuffer();
                    const result = await mammoth.convertToHtml({ arrayBuffer });
                    
                    if (result.value) {
                      const htmlContent = result.value;
                      setContent(htmlContent);
                      setInitialContent(htmlContent);
                      setFileType('rich-text'); // Passer directement en mode rich-text
                      
                      // Sauvegarder le contenu
                      if (socketRef.current && socketRef.current.connected) {
                        socketRef.current.emit('file-collab:content-change', {
                          projectId,
                          content: htmlContent,
                          userId: user._id
                        });
                      }
                      
                      // Uploader le fichier original au backend avec le contenu HTML extrait
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('content', htmlContent); // Envoyer le contenu HTML extrait
                      try {
                        const uploadRes = await axios.post(
                          `${API_BASE}/projects/${projectId}/collaborative-file/upload`,
                          formData,
                          {
                            headers: {
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          }
                        );
                        if (uploadRes.data.success) {
                          // Sauvegarder aussi via l'endpoint save pour s'assurer que le contenu est bien stocké
                          await axios.post(
                            `${API_BASE}/projects/${projectId}/collaborative-file/save`,
                            {
                              content: htmlContent,
                              fileName: file.name
                            },
                            {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }
                          );
                          console.log('Fichier Word uploadé et contenu sauvegardé avec succès');
                        }
                      } catch (err) {
                        console.error('Erreur upload fichier Word:', err);
                        alert('Erreur lors de l\'upload du fichier Word');
                      }
                    } else {
                      alert('Erreur lors de la conversion du fichier Word');
                    }
                  } catch (err: any) {
                    console.error('Erreur conversion Word:', err);
                    alert('Erreur lors de la conversion du fichier Word: ' + (err.message || 'Erreur inconnue'));
                  }
                } else {
                  // Fichier texte
                  const text = await file.text();
                  setContent(text);
                  setInitialContent(text);
                  setFileType('text');
                }
              }}
            />
          </label>

          {/* Utilisateurs actifs avec design moderne */}
          {activeUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Users className="h-4 w-4 text-green-600" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                </div>
                <span className="text-sm font-medium text-green-700">
                  {activeUsers.length} {activeUsers.length > 1 ? 'actifs' : 'actif'}
                </span>
              </div>
              <div className="flex -space-x-2 ml-1">
                {activeUsers.slice(0, 4).map((u, index) => (
                  <div
                    key={u.userId}
                    className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title={u.userName}
                    style={{ zIndex: 10 - index }}
                  >
                    {u.userName.charAt(0).toUpperCase()}
                  </div>
                ))}
                {activeUsers.length > 4 && (
                  <div className="w-7 h-7 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-sm">
                    +{activeUsers.length - 4}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !connected}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md font-medium"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Sauvegarder</span>
                </>
              )}
            </button>
            <button
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Partager"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-auto">
        {fileType === 'rich-text' ? (
          <RichTextCollaborativeEditor
            projectId={projectId}
            user={user}
            initialContent={initialContent}
            onContentChange={handleRichTextContentChange}
            onUsersChange={setActiveUsers}
          />
        ) : fileType === 'pdf' ? (
          <div className="p-4 h-full">
            {content || fileName.endsWith('.pdf') ? (
              <PDFCollaborativeViewer
                projectId={projectId}
                user={user}
                pdfUrl={content || `${API_BASE}/projects/${projectId}/collaborative-file`}
                onSave={handleSave}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <FileText className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No PDF file loaded
                </h3>
                <p className="text-gray-600 mb-4 text-center max-w-md">
                  Use the "Import" button in the header to load a PDF file.
                  Vous pourrez ensuite le visualiser et l'annoter en collaboration.
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                  <Upload className="h-4 w-4" />
                  <span>Choisir un fichier PDF</span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setFileName(file.name);
                      // Créer une URL locale pour le PDF
                      const fileUrl = URL.createObjectURL(file);
                      setContent(fileUrl);
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        ) : fileType === 'word' ? (
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Import de fichier Word
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                Import a Word file (.doc or .docx) to edit it in the rich editor. 
                Styles will be preserved during conversion.
              </p>
              <WordFileImporter
                onImport={async (html) => {
                  setContent(html);
                  setInitialContent(html);
                  setFileType('rich-text');
                  // Envoyer la mise à jour
                  if (socketRef.current && socketRef.current.connected) {
                    socketRef.current.emit('file-collab:content-change', {
                      projectId,
                      content: html,
                      userId: user._id
                    });
                  }
                  // Sauvegarder le contenu au backend
                  try {
                    await axios.post(
                      `${API_BASE}/projects/${projectId}/collaborative-file/save`,
                      {
                        content: html,
                        fileName: fileName || 'document-word-importe.docx'
                      },
                      {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      }
                    );
                    console.log('Contenu Word sauvegardé avec succès');
                  } catch (err) {
                    console.error('Erreur sauvegarde contenu Word:', err);
                  }
                }}
              />
            </div>
            {content && content.includes('<') ? (
              <div className="mt-4">
                <div className="mb-2 text-sm text-gray-600 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Word file converted. You can now edit it:
                </div>
                <RichTextCollaborativeEditor
                  projectId={projectId}
                  user={user}
                  initialContent={content}
                  onContentChange={handleRichTextContentChange}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No Word file imported. Use the form above to import a file.
              </div>
            )}
          </div>
        ) : (
          <div className="p-6">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleContentChange}
              placeholder="Start writing... Changes will be synced in real time with your team."
              className="w-full h-full border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
              style={{ minHeight: '100%' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileCollaborationPage;

